import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypeText {
    headline: string;
  }
  interface PaletteColor {
    softBg?: string;
  }
  interface SimplePaletteColorOptions {
    softBg?: string;
  }
}

export const colors = {
  primary: {
    main: '#0066CC',
    light: '#3385D6',
    dark: '#004C99',
    contrastText: '#FFFFFF',
    softBg: '#F0F5FF',
  },
  secondary: {
    main: '#00A86B',
    light: '#33BA87',
    dark: '#007F50',
    contrastText: '#FFFFFF',
  },
  error: {
    main: '#FF6B35',
    light: '#FF8C5E',
    dark: '#CC5528',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FFA726',
    light: '#FFB951',
    dark: '#F57C00',
    contrastText: '#000000',
  },
  info: {
    main: '#29B6F6',
    light: '#4FC3F7',
    dark: '#0288D1',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#00A86B',
    light: '#33BA87',
    dark: '#007F50',
    contrastText: '#FFFFFF',
  },
  grey: {
    50: '#F5F7FA',
    100: '#E1E8ED',
    200: '#C1C9D2',
    300: '#A1AAB5',
    400: '#818A97',
    500: '#657786',
    600: '#505A66',
    700: '#3C4349',
    800: '#282D31',
    900: '#14171A',
  },
  text: {
    primary: '#14171A',
    secondary: '#657786',
    disabled: '#A1AAB5',
    headline: '#0D1B35',
  },
  background: {
    default: '#EEF2F8',
    paper: '#FFFFFF',
  },
  divider: '#E1E8ED',
};

export const theme = createTheme({
  palette: colors,
  typography: {
    // Body / labels / buttons stay on Plus Jakarta Sans (theme default).
    // Headings (h1..h6) use Bricolage Grotesque — see fontFamily on each variant below.
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    h1: {
      fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
      fontSize: '2.5rem',
      '@media (max-width:600px)': { fontSize: '1.85rem' },
      fontWeight: 500,
      lineHeight: 1.1,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
      fontSize: '2.1rem',
      '@media (max-width:600px)': { fontSize: '1.6rem' },
      fontWeight: 500,
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
      fontSize: '1.75rem',
      '@media (max-width:600px)': { fontSize: '1.4rem' },
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
      fontSize: '1.5rem',
      '@media (max-width:600px)': { fontSize: '1.25rem' },
      fontWeight: 500,
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
    },
    h5: {
      fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.35,
      letterSpacing: '-0.005em',
    },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  spacing: 8,
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@keyframes slideInUp': {
          from: { opacity: 0, transform: 'translateY(12px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes customerStaggerIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to:   { opacity: 1, transform: 'translateY(0)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '@keyframes customerStaggerIn': {
            from: { opacity: 1, transform: 'none' },
            to:   { opacity: 1, transform: 'none' },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '12px 24px',
          minHeight: 44,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            minHeight: 44,
          },
        },
      },
    },
  },
});
